import React, { useCallback, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  X,
  History,
  Package,
  Tag,
  Info,
  Recycle,
  Loader2
} from 'lucide-react';
import InventoryHistoryComponent from './InventoryHistoryComponent';
import leftoverService from '../services/leftoverService';

/**
 * Modal dialog for displaying inventory history
 * Wraps InventoryHistoryComponent in a responsive dialog
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Close dialog callback
 * @param {string} props.productId - Product ID for history lookup
 * @param {Object} props.productInfo - Product information object
 * @param {boolean} props.fullScreen - Force full screen mode
 * @param {number} props.maxWidth - Maximum dialog width
 */
const InventoryHistoryDialog = React.memo(({
  open,
  onClose,
  productId,
  productInfo = null,
  fullScreen = false,
  selectedClientId = 'global'
}) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 640;
  const shouldUseFullScreen = fullScreen || isSmallScreen;
  const effectiveClientId = productInfo?.client_id || selectedClientId;

  const [leftoverModalOpen, setLeftoverModalOpen] = useState(false);
  const [leftovers, setLeftovers] = useState([]);
  const [leftoverLoading, setLeftoverLoading] = useState(false);
  const [leftoverError, setLeftoverError] = useState('');

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleViewLeftovers = useCallback(async () => {
    if (!productInfo?._id && !productInfo?.product_id) {
      setLeftoverError('无法获取产品ID');
      return;
    }

    setLeftoverModalOpen(true);
    setLeftoverLoading(true);
    setLeftoverError('');

    try {
      const parentId = productInfo._id || productInfo.product_id;
      const response = await leftoverService.getByParentId(parentId);
      setLeftovers(response.leftovers || []);
    } catch (err) {
      console.error('Failed to fetch leftovers:', err);
      setLeftoverError('获取余料库存失败: ' + (err.message || '未知错误'));
      setLeftovers([]);
    } finally {
      setLeftoverLoading(false);
    }
  }, [productInfo]);

  const displayProductInfo = {
    name: productInfo?.name || '产品',
    product_id: productInfo?.product_id || productId,
    category: productInfo?.category,
    part_number: productInfo?.part_number,
    remaining_quantity: (() => {
      if (!productInfo) return undefined;
      if (!effectiveClientId || effectiveClientId === 'global') return productInfo.remaining_quantity;
      if (productInfo.client_inventory?.[effectiveClientId]) {
        return productInfo.client_inventory[effectiveClientId].remaining_quantity;
      }
      if (typeof productInfo.client_quantity !== 'undefined' && productInfo.client_quantity !== null) {
        return productInfo.client_quantity;
      }
      return productInfo.remaining_quantity;
    })(),
    ...(productInfo || {})
  };

  const renderProductHeader = () => (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          <span className="text-lg font-semibold truncate">
            {displayProductInfo.name}
          </span>
        </div>

        {displayProductInfo.product_id && (
          <Badge variant="outline" className="flex items-center gap-1">
            <Tag className="h-3 w-3" />
            {displayProductInfo.product_id}
          </Badge>
        )}

        {displayProductInfo.category && (
          <Badge variant="secondary">
            {displayProductInfo.category}
          </Badge>
        )}

        {displayProductInfo.remaining_quantity !== undefined && (
          <Badge className="flex items-center gap-1">
            <Info className="h-3 w-3" />
            库存: {displayProductInfo.remaining_quantity || 0}
          </Badge>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={handleViewLeftovers}
          className="flex items-center gap-1"
        >
          <Recycle className="h-3 w-3" />
          余料库存
        </Button>
      </div>

      {displayProductInfo.part_number && (
        <p className="text-sm text-muted-foreground mt-2">
          零件号: {displayProductInfo.part_number}
        </p>
      )}
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent
          className={`
            ${shouldUseFullScreen ? 'w-screen h-screen max-w-none m-0 rounded-none' : 'max-w-4xl h-[90vh]'}
            flex flex-col overflow-hidden
          `}
        >
          <DialogHeader className="pb-4 border-b bg-muted/50 -mx-6 -mt-6 px-6 pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="h-5 w-5 text-primary" />
                <DialogTitle>库存历史记录</DialogTitle>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                aria-label="关闭对话框"
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col pt-4">
            {renderProductHeader()}

            <Separator className="my-4" />

            <div className="flex-1 overflow-auto min-h-0">
              <InventoryHistoryComponent
                productId={productId}
                productInfo={displayProductInfo}
                compact={shouldUseFullScreen}
                maxHeight={shouldUseFullScreen ? '100%' : 500}
                selectedClientId={effectiveClientId}
              />
            </div>
          </div>

          {shouldUseFullScreen && (
            <DialogFooter className="border-t bg-muted/50 -mx-6 -mb-6 px-6 py-4">
              <Button
                onClick={handleClose}
                className={isMobile ? 'w-full' : ''}
                size={isMobile ? 'lg' : 'default'}
              >
                关闭
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={leftoverModalOpen} onOpenChange={setLeftoverModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Recycle className="h-5 w-5 text-primary" />
                <DialogTitle>余料库存</DialogTitle>
              </div>
              <Badge variant="outline">
                {leftovers.length} 条余料
              </Badge>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-auto py-4">
            {leftoverLoading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
                <span>加载余料库存...</span>
              </div>
            ) : leftoverError ? (
              <div className="text-center py-8 text-destructive">
                {leftoverError}
              </div>
            ) : leftovers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Recycle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>该产品暂无余料库存</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leftovers.map((leftover) => (
                  <div
                    key={leftover._id}
                    className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="font-medium text-sm">{leftover.leftover_code}</span>
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {leftover.status === 'available' ? '可用' : leftover.status === 'consumed' ? '已消耗' : leftover.status}
                        </Badge>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {leftover.length}mm
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-muted-foreground">
                        顶径: {leftover.tip_diameter}mm
                      </div>
                      <div className="text-muted-foreground">
                        数量: {leftover.quantity || 1}
                      </div>
                      <div className="text-muted-foreground">
                        位置: {leftover.location || '-'}
                      </div>
                      <div className="text-muted-foreground">
                        创建: {new Date(leftover.created_at).toLocaleDateString('zh-CN')}
                      </div>
                    </div>
                    {leftover.comments && (
                      <p className="text-xs text-muted-foreground mt-2 border-t pt-2">
                        {leftover.comments}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setLeftoverModalOpen(false)}>
              关闭
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

InventoryHistoryDialog.displayName = 'InventoryHistoryDialog';

export default InventoryHistoryDialog;
